<template>
  <div class="h-full d-flex align-center justify-center">
    <div class="w-full h-full">
      <div :id="ID_DOM" style="width: 100%; height: 100%;" />
    </div>
  </div>
</template>

<script setup>
import {computed, nextTick, onMounted, ref, watch} from "vue";
import {formatarValor, formatarValorMonetario} from "@/helpers";
import * as echarts from "echarts";
import formatarValoresBi from "@/helpers/formatarValoresBi";
import {buscarMovimentacoesEstoqueProdutos} from "@/api/compraPorProdutosGerais/Indicadores";

const props = defineProps({
  filtros: Object,
  update_resize: Number,
})

watch(() => props.update_resize, atualizarGrafico)

watch(() => props.filtros, buscarDadosGrafico)

const ID_DOM = `INDICADOR_MOVIMENTACAO_ESTOQUE_FORNECEDOR`;

onMounted(() => {
  iniciarGrafico();
  buscarDadosGrafico();
});

let myChart;

const info_series = {
  compras: {
    index: 'compras',
    name: 'Compras',
    visao_heranca: true,
  },
  entradas: {
    index: 'entradas',
    name: 'Entradas',
    visao_heranca: true,
  },
  vendas: {
    index: 'vendas',
    name: 'Vendas',
  },
  avaria_em_taxa: {
    index: 'avaria_em_taxa',
    name: 'Taxa de Avaria',
  }
};

function formataSomaValoresIndexSeries(valor) {
  return Number(valor) > 0 ? formatarValoresBi(Number(valor || 0), 1) : '';
}

const label = {
  show: true,
  position: 'top',
  rotate: 90,
  offset: [15, 0],
  fontSize: 11,
  align: 'center',
  color: 'black',
  verticalAlign: 'center',
  textBorderColor: 'none',
  textBorderWidth: 0,
  formatter: function (f) {
    return formataSomaValoresIndexSeries(f.value);
  }
};

const serie_padrao = Array.from({ length: 13 }, () => 0);

const option = ref({
  grid: {
    top: '35px',
    bottom: '3%',
    left: '3%',
    right: '2%',
    containLabel: true,
    shadowOffsetX: {
      show: true,
    }
  },
  graphic: {
    elements: [
      {
        type: 'text',
        left: 5,
        top: 'center',
        rotation: Math.PI / 2,
        style: {
          text: 'Análise de Compras vs Vendas',
          fontSize: 12,
          fontFamily: 'Roboto',
          fontWeight: 600,
          color: '#000000'
        }
      }
    ]
  },
  legend: {
    icon: 'rect',
    itemWidth: 13,
    itemHeight: 13,
    show: true,
    data: ['Compras', 'Entradas', 'Vendas'],
  },
  tooltip: {
    trigger: 'axis',
    axisPointer: {
      type: 'cross',
      label: {
        show: false,
        formatter: label => {
          if (label.axisDimension === 'y') {
            return formatarValor(label.value);
          }

          return label.value;
        },
        confine: true
      }
    },
    formatter: function (params) {
      var tooltipText = '';

      const y_value = params[0].axisValue;

      if (label.axisDim === 'y') {
        tooltipText += formatarValor(y_value) + '<br>';
      } else {
        tooltipText += y_value + '<br>';
      }

      params.forEach(function (item) {
        if (item.seriesName === info_series.avaria_em_taxa.name) {
          if (avarias.value.valores.every(valor => Number(valor || 0) === 0)) return;

          tooltipText += `${info_series.avaria_em_taxa.name}: <br>
            <span class="pl-3 font-13">${item.marker} Em percentual: ${formatarValor(item.data)}%</span> <br>
            <span class="pl-3 font-13">${item.marker} Em volume: ${formatarValor(avarias.value.volumes[item.dataIndex])}</span> <br>
            <span class="pl-3 font-13">${item.marker} Em valor: R$ ${formatarValor(avarias.value.valores[item.dataIndex])}</span> <br>
          `;
          return;
        }

        tooltipText += item.marker + item.seriesName + ': ' + formatarValorMonetario(item.data) + '<br>';
      });

      return tooltipText;
    },
    valueFormatter: valor => formatarValorMonetario(valor)
  },
  xAxis: {
    type: 'category',
    axisLabel: {
      fontSize: 11,
      color: 'black',
      interval: 0,
      formatter: valor => {
        const [mes_str, ano] = `${valor}`.split(' ');

        if (!mes_str || !ano) {
          return 'Mês';
        }

        return `${mes_str.substring(0, 3)}/${ano.substring(2, 4)}`;
      },
    },
    boundaryGap: true,
    splitLine: {
      show: true
    },
    data:  Array.from({ length: 13 }, () => 'Mês'),
  },
  yAxis: [
    {
      index: 'valores',
      type: 'value',
      position: 'left',
      splitLine: {
        show: true
      },
      axisLabel: {
        formatter: valor => formataSomaValoresIndexSeries(valor)
      },
      max: 10
    },
    {
      index: info_series.avaria_em_taxa.index,
      type: 'value',
      position: 'right',
      show: false,
      min: 0,
      max: 100,
      splitLine: {
        show: false
      }
    }
  ],
  series: [
    {
      index: info_series.compras.index,
      name: info_series.compras.name,
      type: "bar",
      color: "rgba(4, 53, 84, 0.6)",
      stack: 'compras',
      label,
      data: serie_padrao,
    },
    {
      index: info_series.entradas.index,
      name: info_series.entradas.name,
      type: "bar",
      color: "rgba(4, 53, 84, 1)",
      stack: 'entradas',
      label,
      data: serie_padrao,
    },
    {
      index: info_series.vendas.index,
      name: info_series.vendas.name,
      type: "bar",
      color: "rgba(214, 14, 43,1)",
      stack: 'vendas',
      label,
      data: serie_padrao,
    },
    {
      index: info_series.avaria_em_taxa.index,
      name: info_series.avaria_em_taxa.name,
      type: "line",
      yAxisIndex: 1,
      smooth: 0.3,
      color: "#993399",
      lineStyle: {
        width: 2
      },
      z: 2,
      showSymbol: false,
      data: serie_padrao.map(() => null)
    }
  ]
});

async function iniciarGrafico() {
  const chartDom = document.getElementById(ID_DOM);

  myChart = echarts.init(chartDom);
  myChart.setOption(option.value);

  await nextTick();
  myChart.resize();

  window.addEventListener('resize', () => myChart.resize());
}

const avarias = ref({
  valores: [],
  volumes: [],
});

function buscarDadosGrafico() {
  buscarMovimentacoesEstoqueProdutos({filtros: props.filtros})
    .then(movimentacoes => {
      for (const serie of option.value.series) {
        serie.data = movimentacoes[serie.index]
      }

      avarias.value.volumes = movimentacoes.avaria_em_volume;
      avarias.value.valores = movimentacoes.avaria_em_valor;
      option.value.xAxis.data = movimentacoes.referencias;

      atualizarGrafico();
    })
}

const MAIOR_VALOR_SERIES_VALORES_COMPUTED = computed(() => option.value.series.reduce((a, b) => Math.max(a, ...b.data), 0));

function atualizarGrafico() {
  const y_axis_valores = option.value.yAxis.find(item => item.index === 'valores');
  y_axis_valores.max = MAIOR_VALOR_SERIES_VALORES_COMPUTED.value + (MAIOR_VALOR_SERIES_VALORES_COMPUTED.value * 0.25);

  setConfigYAxisPecentuais();

  myChart.setOption(option.value);
  myChart.resize();
}

const maior_valor_percentual_computed = computed(() => {
  const serie_avaria_em_taxa = option.value.series.find(item => item.index === info_series.avaria_em_taxa.index);
  const maior_valor = Math.max(...serie_avaria_em_taxa.data);
  return Math.ceil(maior_valor + (maior_valor * 0.2));
});

function setConfigYAxisPecentuais() {
  const y_axis_avaria_em_taxa = option.value.yAxis.find(item => item.index === info_series.avaria_em_taxa.index);

  if (maior_valor_percentual_computed.value > 0) {
    y_axis_avaria_em_taxa.max = maior_valor_percentual_computed.value;
    y_axis_avaria_em_taxa.show = true;
    option.value.grid.right = '2%';
  } else {
    y_axis_avaria_em_taxa.max = 100;
    y_axis_avaria_em_taxa.show = false;
    option.value.grid.right = '-20px';
  }
}
</script>
